// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

// Soft UI Dashboard PRO React example components
import DefaultPricingCard from "examples/Cards/PricingCards/DefaultPricingCard";
import { useMemo, useState } from "react";
import CheckoutFormGenerator from "../../../../../examples/Checkout";
import { getUserIsLogin } from "../../../../../services/auth.services";
import { getAppPath } from "../../../../../services/app-path.service";
import { toast } from "react-toastify";
import { activateFreeTrial } from "../../../../../services/subscription.service";

const FREE_PLAN_ID = (process.env.REACT_APP_STRIPE_FREE_PLAN_ID || "plan_U7BCf1k5ZLEqZ5").trim();
const PAYG_PLAN_ID = process.env.REACT_APP_STRIPE_PAYG_PLAN_ID || "";
const INDIVIDUAL_PLAN_ID = process.env.REACT_APP_STRIPE_INDIVIDUAL_PLAN_ID || "";

function PricingCards() {
  const [show, setShow] = useState(false);
  const [product, setProduct] = useState("");
  const [activatingFreeTrial, setActivatingFreeTrial] = useState(false);
  const isLoggedIn = getUserIsLogin();

  const individualPlanTarget = useMemo(() => {
    if (INDIVIDUAL_PLAN_ID.trim() !== "") {
      return INDIVIDUAL_PLAN_ID.trim();
    }
    return "contact-us";
  }, []);
  const paygPlanTarget = useMemo(() => {
    if (PAYG_PLAN_ID.trim() !== "") {
      return PAYG_PLAN_ID.trim();
    }
    return "contact-us";
  }, []);

  const onSelect = async (_, planId) => {
    if (planId === "contact-us") {
      window.open("https://rbranalytics.io/#contactUs", "_blank");
      return;
    }
    if (!isLoggedIn) {
      window.location.href = getAppPath("/authentication/login");
      return;
    }
    if (planId === FREE_PLAN_ID) {
      if (activatingFreeTrial) {
        return;
      }
      setActivatingFreeTrial(true);
      try {
        await activateFreeTrial(planId);
        localStorage.setItem("has_plan", "1");
        localStorage.setItem("active_plan", planId);
        toast.success("Free trial activated.", { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 2000, hideProgressBar: false });
        window.location.href = getAppPath("/dashboards/home");
      } catch (e) {
        const message = e?.response?.data?.status || "Could not activate free trial";
        toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 2500, hideProgressBar: false });
      } finally {
        setActivatingFreeTrial(false);
      }
      return;
    }
    setProduct(planId);
    setShow(true);
  };

  const closeCheckout = () => {
    setShow(false);
    setProduct("");
  };

  return (
    <SoftBox position="relative" zIndex={10} mt={{ xs: -10, lg: -20 }} px={{ xs: 1, sm: 0 }}>
      <CheckoutFormGenerator show={show} product={product} onClose={closeCheckout} />
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={6} lg={3}>
          <DefaultPricingCard
            badge={{ color: "secondary", label: "Free Trial" }}
            price={{ currency: "", value: "Free", text: "/ 3 days" }}
            specifications={[
              { label: "3 Days Trial Access", includes: true },
              { label: "1 Report per Day", includes: true },
              { label: "Individual Plan", includes: true },
              { label: "Sample Data", includes: true },
              { label: "Guidance Document", includes: true },
            ]}
            id={FREE_PLAN_ID}
            todoFunc={onSelect}
            action={{ color: "dark", label: isLoggedIn ? (activatingFreeTrial ? "Starting..." : "Start Trial") : "Sign Up" }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DefaultPricingCard
            badge={{ color: "secondary", label: "Pay As You Go" }}
            price={{ currency: "USD ", value: "199", text: "/ run / report" }}
            specifications={[
              { label: "Charged Per Completed Run", includes: true },
              { label: "No Annual Commitment", includes: true },
              { label: "Sample Data", includes: true },
              { label: "Guidance Document", includes: true },
              { label: "Best for Occasional Analyses", includes: true },
            ]}
            id={paygPlanTarget}
            todoFunc={onSelect}
            action={{
              color: "dark",
              label: paygPlanTarget === "contact-us" ? "Contact Us" : isLoggedIn ? "Select" : "Sign Up",
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DefaultPricingCard
            badge={{ color: "secondary", label: "Individual" }}
            price={{ currency: "USD ", value: "1,499", text: "/ year / user" }}
            specifications={[
              { label: "Sample Data", includes: true },
              { label: "Guidance Document", includes: true },
              { label: "Unlimited Analyses", includes: true },
              { label: "Professional Consulting Support", includes: true },
              { label: "For Single User", includes: true },
            ]}
            id={individualPlanTarget}
            todoFunc={onSelect}
            action={{
              color: "dark",
              label: individualPlanTarget === "contact-us" ? "Contact Us" : isLoggedIn ? "Select" : "Sign Up",
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DefaultPricingCard
            badge={{ color: "secondary", label: "Team & Corporate" }}
            price={{ currency: "", value: "Custom", text: "USD quote" }}
            specifications={[
              { label: "Multi-Site Team Deployment", includes: true },
              { label: "Engineering + Reliability Advisory", includes: true },
              { label: "Water / Oil & Gas Workflow Alignment", includes: true },
              { label: "Private Onboarding & Training", includes: true },
              { label: "Custom Enterprise Terms", includes: true },
            ]}
            id="contact-us"
            todoFunc={onSelect}
            action={{ color: "dark", label: "Contact Us" }}
          />
        </Grid>
      </Grid>

      <Grid container sx={{ marginTop: "20px" }} justifyContent="center">
        All prices are in USD. Local taxes are extra.
      </Grid>
    </SoftBox>
  );
}

export default PricingCards;
